import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { Trash2, Plus, Save, Edit2, Loader, Calendar } from 'lucide-react';
import { Event } from '../../../types';
import ImageUploader from '../common/ImageUploader';

const EventManager: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const q = query(collection(db, 'events'), orderBy('date', 'asc'));
      const querySnapshot = await getDocs(q);
      const list: Event[] = [];
      querySnapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() } as Event);
      });
      setEvents(list);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setSubtitle('');
    setDate('');
    setLocation('');
    setDescription('');
    setImageUrl('');
    setEditingId(null);
  };

  const handleEdit = (event: Event) => {
    setEditingId(event.id);
    setTitle(event.title);
    setSubtitle(event.subtitle || '');
    setDate(event.date); // Assumed ISO string or compatible
    setLocation(event.location || '');
    setDescription(event.description);
    setImageUrl(event.imageUrl);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await deleteDoc(doc(db, 'events', id));
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !date) return;

    try {
      const finalImageUrl = imageUrl || '/assets/placeholder.svg';

      const eventData = {
        title,
        subtitle,
        date,
        location,
        description,
        imageUrl: finalImageUrl,
        status: new Date(date) > new Date() ? 'upcoming' : 'past',
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(db, 'events', editingId), eventData);
      } else {
        await addDoc(collection(db, 'events'), {
          ...eventData,
          createdAt: serverTimestamp()
        });
      }

      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  if (loading) return <div className="py-8"><Loader className="animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      
      {/* List */}
      <div className="lg:col-span-2 space-y-4">
         <h2 className="font-heading font-bold text-lg mb-4">Events List</h2>
         {events.length === 0 && <p className="text-gray-500 italic text-sm">No events found.</p>}
         {events.map(event => (
           <div key={event.id} className="bg-white border border-gray-200 p-4 flex gap-4 items-center group">
              <div className="bg-gray-100 w-16 h-16 flex flex-col items-center justify-center border border-gray-200">
                 <span className="text-xs font-bold text-gray-400 uppercase">{new Date(event.date).toLocaleString('default', { month: 'short' })}</span>
                 <span className="text-xl font-black">{new Date(event.date).getDate()}</span>
              </div>
              <div className="flex-1">
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded inline-block mb-1 ${event.status === 'upcoming' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>{event.status}</span>
                <h3 className="font-heading font-bold text-lg leading-tight">{event.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{event.location} • {new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
              </div>
              <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                 <button onClick={() => handleEdit(event)} className="p-2 text-gray-400 hover:text-black hover:bg-gray-50 rounded"><Edit2 size={16} /></button>
                 <button onClick={() => handleDelete(event.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
              </div>
           </div>
         ))}
      </div>

      {/* Editor */}
      <div className="bg-gray-50 p-6 border border-gray-200 h-fit sticky top-6">
        <h2 className="font-heading font-bold text-lg mb-6 flex items-center gap-2">
           {editingId ? <Edit2 size={18}/> : <Plus size={18}/>}
           {editingId ? 'Edit Event' : 'New Event'}
        </h2>
        
        <form onSubmit={handleSave} className="space-y-4">
           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Title</label>
             <input className="w-full p-2 border border-gray-300 font-heading font-bold" value={title} onChange={e => setTitle(e.target.value)} placeholder="Event Name" required />
           </div>
           
           <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Date & Time</label>
               <input type="datetime-local" className="w-full p-2 border border-gray-300 text-sm" value={date} onChange={e => setDate(e.target.value)} required />
             </div>
             <div>
               <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Location</label>
               <input className="w-full p-2 border border-gray-300 text-sm" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Venice Beach" />
             </div>
           </div>

           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Subtitle</label>
             <input className="w-full p-2 border border-gray-300 text-sm" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Short tag (e.g. Community Meetup)" />
           </div>

           <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Description</label>
              <textarea className="w-full p-2 border border-gray-300 text-sm" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
           </div>

           <div>
             <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">Event Image</label>
             <ImageUploader
               value={imageUrl}
               onChange={setImageUrl}
               pathPrefix="events"
               aspectClass="aspect-video"
               onError={(text) => console.error(text)}
             />
           </div>

           <div className="pt-4 flex gap-2">
             <button type="submit" className="flex-1 bg-black text-white py-3 font-heading font-bold uppercase tracking-widest text-xs hover:bg-gray-800">
               {editingId ? 'Update Event' : 'Save Event'}
             </button>
             {editingId && (
               <button type="button" onClick={resetForm} className="px-4 border border-gray-300 text-xs font-bold uppercase hover:bg-gray-100">
                 Cancel
               </button>
             )}
           </div>
        </form>
      </div>
    </div>
  );
};

export default EventManager;
